/**
 * Corredores curados (modelo genérico, dados versionados).
 *
 * Um "corredor" é uma rota famosa cuja GEOMETRIA faz parte da experiência:
 * a Estrada Nacional 2, a Route 66, a Ruta 40, a Great Ocean Road. Fazer o
 * percurso por autoestrada entre as mesmas cidades NÃO é fazer a rota.
 *
 * O modelo é neutro: qualquer corredor no mundo entra por dados, nunca por
 * regras específicas de país. Nada aqui é inventado — apenas localidades
 * oficiais do percurso e as suas coordenadas.
 */

export type CorridorWaypoint = { name: string; lat: number; lng: number };

export type CorridorDurationPreset = {
  days: number;
  label: string;
  /** Descrição honesta do ritmo. */
  note: string;
};

export type CuratedCorridor = {
  id: string;
  /** Template curado equivalente, quando existe. */
  templateId?: string;
  name: string;
  countryCodes: string[];
  aliases: string[];
  /** Localidades oficiais por ordem, de uma ponta à outra. */
  waypoints: CorridorWaypoint[];
  approxKm: number;
  /** Desvio máximo aceitável face ao corredor, em km. */
  maxDeviationKm: number;
  durationPresets: CorridorDurationPreset[];
  officialUrl?: string;
  /** Vias que descaracterizam o percurso quando substituem o corredor. */
  disallowedShortcuts: string[];
  verifiedAt: string;
};

export const CURATED_CORRIDORS: CuratedCorridor[] = [
  {
    id: "pt-en2",
    templateId: "pt-estrada-nacional-2",
    name: "Estrada Nacional 2",
    countryCodes: ["PT"],
    aliases: [
      "estrada nacional 2",
      "nacional 2",
      "en2",
      "n2",
      "route 66 portuguesa",
      "portuguese route 66",
      "national road 2",
    ],
    waypoints: [
      { name: "Estrada Nacional 2 (EN2) — Km 0 (Chaves)", lat: 41.7367327, lng: -7.4649072 },
      { name: "Vidago", lat: 41.6417, lng: -7.575 },
      { name: "Pedras Salgadas", lat: 41.5333, lng: -7.5917 },
      { name: "Vila Pouca de Aguiar", lat: 41.4996, lng: -7.6407 },
      { name: "Vila Real", lat: 41.3006, lng: -7.7441 },
      { name: "Miradouro da Cumieira", lat: 41.2303042, lng: -7.7665683 },
      { name: "Casa do Cantoneiro", lat: 41.2256237, lng: -7.7712882 },
      { name: "Miradouro do Fial — Santa Marta de Penaguião", lat: 41.2098, lng: -7.786 },
      { name: "Peso da Régua", lat: 41.1621, lng: -7.7889 },
      { name: "Quinta do Cabouco", lat: 41.1500874, lng: -7.7840646 },
      { name: "Café S. Tiago", lat: 41.1257231, lng: -7.7865285 },
      { name: "Lamego", lat: 41.0976, lng: -7.8103 },
      { name: "Penude", lat: 41.0788857, lng: -7.8495174 },
      { name: "Senhora dos Bons Caminhos", lat: 41.0402822, lng: -7.8681576 },
      { name: "Mezio — Restaurante Típico", lat: 40.9796891, lng: -7.8866874 },
      { name: "Pacol", lat: 40.9162414, lng: -7.927759 },
      { name: "Braços de Lá — Escola Básica Castro Daire 1", lat: 40.9078622, lng: -7.9321308 },
      { name: "Castro Daire", lat: 40.8994, lng: -7.9339 },
      { name: "Taska Chik — Restaurante", lat: 40.898066, lng: -7.9337489 },
      { name: "Viseu", lat: 40.6566, lng: -7.9125 },
      { name: "Fail — Torre (Viseu)", lat: 40.5877, lng: -7.9484 },
      { name: "Sabugosa Baixo", lat: 40.549, lng: -7.988 },
      { name: "Ecopista do Dão — Tondela", lat: 40.5175, lng: -8.077 },
      { name: "Tondela", lat: 40.5165, lng: -8.0805 },

      { name: "Kat-Kero", lat: 40.4927136, lng: -8.0885446 },
      { name: "Dardavaz — Garrucha", lat: 40.4770971, lng: -8.0964849 },
      { name: "Taberna Baptista — Petiscaria e Produtos Regionais", lat: 40.4339971, lng: -8.1186026 },
      { name: "A Lampreia — Foz do Dão (Santa Comba Dão)", lat: 40.4102, lng: -8.1381 },
      { name: "Santa Comba Dão", lat: 40.3958, lng: -8.1289 },
      { name: "Núcleo Sporting de Santa Comba Dão", lat: 40.3966153, lng: -8.1295959 },
      { name: "Memorial das Invasões Francesas", lat: 40.3923082, lng: -8.1286647 },
      { name: "Casa onde nasceu Professor Doutor Oliveira Salazar", lat: 40.3878054, lng: -8.1246066 },
      { name: "Venda do Sebo — Jardim Rainha Santa", lat: 40.3750879, lng: -8.1209271 },
      { name: "Restaurante Mondego — Natália Henriques", lat: 40.3036997, lng: -8.2299571 },
      { name: "Miradouro da Livraria do Mondego", lat: 40.2842661, lng: -8.2639482 },
      { name: "Penacova", lat: 40.2703, lng: -8.2817 },
      { name: "Posto de Turismo N2 Penacova", lat: 40.2693328, lng: -8.2732355 },
      { name: "Ronqueira", lat: 40.2520339, lng: -8.2774276 },
      { name: "Vila Nova de Poiares", lat: 40.2136, lng: -8.2589 },
      { name: "Góis", lat: 40.1556, lng: -8.1114 },
      { name: "Restaurante Beira Rio — Góis", lat: 40.1620142, lng: -8.1116574 },
      { name: "Fonte EN2 — Álvares", lat: 40.0736297, lng: -8.0758883 },
      { name: "Miradouro do Cabril", lat: 40.0878318, lng: -7.8719508 },
      { name: "Estrada Nacional 2 (EN2) — Km 300", lat: 40.024612, lng: -8.0879766 },

      { name: "Pedrógão Pequeno — Bairro do Cabril", lat: 39.9083938, lng: -8.1323047 },
      { name: "Painho", lat: 39.8935557, lng: -8.129689 },
      { name: "Marco Nacional 2 — Km 333", lat: 39.873139, lng: -8.125594 },
      { name: "Café 50", lat: 39.8102821, lng: -8.1081113 },
      { name: "Sertã", lat: 39.8056905, lng: -8.0978445 },
      { name: "Vila de Rei", lat: 39.6716, lng: -8.1442 },
      { name: "Fonte do Pisco — Sardoal", lat: 39.5397, lng: -8.1583 },
      { name: "Barreiras do Tejo (Av. da Portagem) — Abrantes", lat: 39.4538396, lng: -8.1934695 },
      { name: "Marco Quilométrico EN2 — Mora", lat: 38.9441885, lng: -8.1654674 },
      { name: "Av. Gago Coutinho — Montemor-o-Novo", lat: 38.6496021, lng: -8.2215803 },

      { name: "Estrada Nacional 2 (EN2) — Km 565 (Torrão Norte)", lat: 38.312, lng: -8.227 },
      { name: "Parque de Lazer da Fonte Nova — Ferreira do Alentejo", lat: 38.0498131, lng: -8.1173761 },
      { name: "Aljustrel", lat: 37.8781, lng: -8.1656 },
      { name: "Castro Verde", lat: 37.6989, lng: -8.0839 },
      { name: "Almodôvar", lat: 37.5107, lng: -8.0623 },
      { name: "Bicas da Serra (Casa Velha Cortiça)", lat: 37.4813, lng: -8.0331 },
      { name: "Vale da Amoreira", lat: 37.4561, lng: -8.0081 },
      { name: "Parque de merendas de Dogueno", lat: 37.4267183, lng: -7.9788916 },
      { name: "Boiça", lat: 37.2217076, lng: -7.9445647 },
      { name: "Cancela (Sul) — Estói", lat: 37.115414, lng: -7.9067016 },
      { name: "Estrada Nacional 2 (EN2) — Km 738 (Faro)", lat: 37.015222, lng: -7.9353939 },

    ],

    approxKm: 738,
    maxDeviationKm: 25,
    durationPresets: [
      {
        days: 3,
        label: "Percurso rápido",
        note: "Cerca de 250 km por dia. Cumpre o corredor, mas quase sem visitas.",
      },
      {
        days: 5,
        label: "Ritmo equilibrado",
        note: "Cerca de 150 km por dia, com tempo para vilas, miradouros e refeições.",
      },
      {
        days: 7,
        label: "Com visitas e descanso",
        note: "Cerca de 105 km por dia, com paragens culturais e gastronómicas em cada etapa.",
      },
    ],
    officialUrl: "https://www.rotan2.pt/",
    disallowedShortcuts: ["A1", "A2", "A4", "A24", "IP3", "A22"],
    verifiedAt: "2026-01-01",
  },
  {
    id: "us-route-66",
    name: "Route 66",
    countryCodes: ["US"],
    aliases: ["route 66", "ruta 66", "rota 66", "us 66", "historic route 66", "mother road"],
    waypoints: [
      { name: "Chicago", lat: 41.8781, lng: -87.6298 },
      { name: "Springfield, Illinois", lat: 39.7817, lng: -89.6501 },
      { name: "St. Louis", lat: 38.627, lng: -90.1994 },
      { name: "Springfield, Missouri", lat: 37.2089, lng: -93.2923 },
      { name: "Tulsa", lat: 36.154, lng: -95.9928 },
      { name: "Oklahoma City", lat: 35.4676, lng: -97.5164 },
      { name: "Amarillo", lat: 35.222, lng: -101.8313 },
      { name: "Santa Fe", lat: 35.687, lng: -105.9378 },
      { name: "Albuquerque", lat: 35.0844, lng: -106.6504 },
      { name: "Gallup", lat: 35.5281, lng: -108.7426 },
      { name: "Flagstaff", lat: 35.1983, lng: -111.6513 },
      { name: "Kingman", lat: 35.1894, lng: -114.053 },
      { name: "Barstow", lat: 34.8958, lng: -117.0173 },
      { name: "Santa Monica", lat: 34.0195, lng: -118.4912 },
    ],
    approxKm: 3940,
    maxDeviationKm: 40,
    durationPresets: [
      { days: 10, label: "Ritmo rápido", note: "Cerca de 400 km por dia. Cumpre o corredor com poucas visitas." },
      { days: 14, label: "Ritmo equilibrado", note: "Cerca de 280 km por dia, com paragens icónicas." },
      { days: 21, label: "Com visitas e descanso", note: "Cerca de 190 km por dia, com tempo para desvios curtos." },
    ],
    officialUrl: "https://www.nps.gov/subjects/travelroute66/index.htm",
    disallowedShortcuts: ["I-40", "I-44", "I-55"],
    verifiedAt: "2026-01-01",
  },
  {
    id: "gb-nc500",
    name: "North Coast 500",
    countryCodes: ["GB"],
    aliases: ["north coast 500", "nc500", "nc 500", "costa norte 500"],
    waypoints: [
      { name: "Inverness", lat: 57.4778, lng: -4.2247 },
      { name: "Dingwall", lat: 57.5951, lng: -4.4249 },
      { name: "Ullapool", lat: 57.8955, lng: -5.1601 },
      { name: "Lochinver", lat: 58.1467, lng: -5.2469 },
      { name: "Durness", lat: 58.5691, lng: -4.7462 },
      { name: "Thurso", lat: 58.5936, lng: -3.5219 },
      { name: "John o' Groats", lat: 58.6373, lng: -3.0689 },
      { name: "Wick", lat: 58.4396, lng: -3.0895 },
      { name: "Dornoch", lat: 57.8806, lng: -4.0281 },
      { name: "Inverness", lat: 57.4778, lng: -4.2247 },
    ],
    approxKm: 830,
    maxDeviationKm: 25,
    durationPresets: [
      { days: 5, label: "Ritmo rápido", note: "Cerca de 165 km por dia em estradas estreitas." },
      { days: 7, label: "Ritmo equilibrado", note: "Cerca de 120 km por dia, com miradouros e praias." },
      { days: 10, label: "Com visitas e descanso", note: "Cerca de 85 km por dia, com trilhos e castelos." },
    ],
    officialUrl: "https://northcoast500.com/",
    disallowedShortcuts: ["A9"],
    verifiedAt: "2026-01-01",
  },
  {
    id: "de-romantische-strasse",
    name: "Romantische Straße",
    countryCodes: ["DE"],
    aliases: ["romantic road", "romantische strasse", "romantische straße", "estrada romantica", "rota romantica", "ruta romantica", "route romantique", "strada romantica"],
    waypoints: [
      { name: "Würzburg", lat: 49.7913, lng: 9.9534 },
      { name: "Tauberbischofsheim", lat: 49.6222, lng: 9.6625 },
      { name: "Bad Mergentheim", lat: 49.4939, lng: 9.7736 },
      { name: "Rothenburg ob der Tauber", lat: 49.3777, lng: 10.1789 },
      { name: "Dinkelsbühl", lat: 49.0708, lng: 10.3197 },
      { name: "Nördlingen", lat: 48.851, lng: 10.4877 },
      { name: "Donauwörth", lat: 48.7183, lng: 10.7783 },
      { name: "Augsburg", lat: 48.3705, lng: 10.8978 },
      { name: "Landsberg am Lech", lat: 48.0489, lng: 10.8804 },
      { name: "Schongau", lat: 47.8125, lng: 10.8969 },
      { name: "Füssen", lat: 47.5716, lng: 10.7018 },
    ],
    approxKm: 460,
    maxDeviationKm: 20,
    durationPresets: [
      { days: 3, label: "Ritmo rápido", note: "Cerca de 150 km por dia, com paragens curtas." },
      { days: 5, label: "Ritmo equilibrado", note: "Cerca de 90 km por dia, com vilas medievais." },
      { days: 7, label: "Com visitas e descanso", note: "Cerca de 65 km por dia, com castelos e museus." },
    ],
    officialUrl: "https://www.romantischestrasse.de/",
    disallowedShortcuts: ["A7", "A8"],
    verifiedAt: "2026-01-01",
  },
  {
    id: "au-great-ocean-road",
    name: "Great Ocean Road",
    countryCodes: ["AU"],
    aliases: ["great ocean road", "grande estrada oceanica", "gran carretera oceanica"],
    waypoints: [
      { name: "Torquay", lat: -38.3333, lng: 144.3167 },
      { name: "Anglesea", lat: -38.4083, lng: 144.185 },
      { name: "Lorne", lat: -38.5406, lng: 143.9744 },
      { name: "Apollo Bay", lat: -38.7551, lng: 143.6702 },
      { name: "Port Campbell", lat: -38.6197, lng: 142.9944 },
      { name: "Warrnambool", lat: -38.3818, lng: 142.4879 },
      { name: "Allansford", lat: -38.3853, lng: 142.5906 },
    ],
    approxKm: 243,
    maxDeviationKm: 15,
    durationPresets: [
      { days: 2, label: "Ritmo rápido", note: "Cerca de 120 km por dia, com as paragens principais." },
      { days: 3, label: "Ritmo equilibrado", note: "Cerca de 80 km por dia, com miradouros e trilhos." },
      { days: 4, label: "Com visitas e descanso", note: "Cerca de 60 km por dia, com praias e vida selvagem." },
    ],
    officialUrl: "https://visitgreatoceanroad.org.au/",
    disallowedShortcuts: ["A1", "Princes Highway"],
    verifiedAt: "2026-01-01",
  },
];

const fold = (value: string) =>
  (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/gu, "")
    .toLowerCase();

/** Reconhece um corredor curado nomeado no texto do pedido. */
export function findCorridorInText(text: string): CuratedCorridor | null {
  const folded = fold(text);
  if (!folded.trim()) return null;
  for (const corridor of CURATED_CORRIDORS) {
    for (const alias of corridor.aliases) {
      const re = new RegExp(`\\b${fold(alias).replace(/[.*+?^${}()|[\]\\]/gu, "\\$&")}\\b`, "u");
      if (re.test(folded)) return corridor;
    }
  }
  return null;
}

export function corridorById(id: string | null | undefined): CuratedCorridor | null {
  if (!id) return null;
  return CURATED_CORRIDORS.find((corridor) => corridor.id === id) ?? null;
}

export function corridorForTemplate(templateId: string | null | undefined): CuratedCorridor | null {
  if (!templateId) return null;
  return CURATED_CORRIDORS.find((corridor) => corridor.templateId === templateId) ?? null;
}

const R = 6371;
function toRad(value: number) {
  return (value * Math.PI) / 180;
}

function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h =
    Math.sin(dLat / 2) ** 2 + Math.sin(dLng / 2) ** 2 * Math.cos(lat1) * Math.cos(lat2);
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Distância aproximada de um ponto ao segmento A-B (projeção plana local). */
function distanceToSegmentKm(
  point: { lat: number; lng: number },
  a: CorridorWaypoint,
  b: CorridorWaypoint,
): number {
  const kx = Math.cos(toRad((a.lat + b.lat) / 2)) * 111.32;
  const ky = 110.57;
  const px = point.lng * kx;
  const py = point.lat * ky;
  const ax = a.lng * kx;
  const ay = a.lat * ky;
  const bx = b.lng * kx;
  const by = b.lat * ky;
  const dx = bx - ax;
  const dy = by - ay;
  const lengthSq = dx * dx + dy * dy;
  if (lengthSq === 0) return haversineKm(point, a);
  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lengthSq));
  const cx = ax + t * dx;
  const cy = ay + t * dy;
  return Math.hypot(px - cx, py - cy);
}

/** Desvio (km) de um ponto face à geometria oficial do corredor. */
export function deviationFromCorridorKm(
  corridor: CuratedCorridor,
  point: { lat: number; lng: number },
): number {
  let best = Number.POSITIVE_INFINITY;
  for (let i = 0; i < corridor.waypoints.length - 1; i++) {
    const distance = distanceToSegmentKm(point, corridor.waypoints[i], corridor.waypoints[i + 1]);
    if (distance < best) best = distance;
  }
  return Number.isFinite(best) ? Math.round(best * 10) / 10 : 0;
}

export function isWithinCorridor(
  corridor: CuratedCorridor,
  point: { lat: number; lng: number },
  maxKm = corridor.maxDeviationKm,
): boolean {
  return deviationFromCorridorKm(corridor, point) <= maxKm;
}

export type CorridorStage = {
  day: number;
  from: string;
  to: string;
  approxKm: number;
  waypoints: CorridorWaypoint[];
};

/**
 * Divide o corredor por dias respeitando SEMPRE a ordem geográfica: cada dia
 * avança no corredor, nunca repete a base do dia anterior e nunca recua.
 */
export function buildCorridorStages(
  corridor: CuratedCorridor,
  days: number,
  direction: "forward" | "reverse" = "forward",
): CorridorStage[] {
  const points =
    direction === "reverse" ? [...corridor.waypoints].reverse() : [...corridor.waypoints];
  const total = Math.max(1, Math.min(days, points.length - 1));
  const perStage = (points.length - 1) / total;
  const stages: CorridorStage[] = [];
  for (let index = 0; index < total; index++) {
    const startIdx = Math.round(index * perStage);
    const endIdx = Math.round((index + 1) * perStage);
    const slice = points.slice(startIdx, endIdx + 1);
    if (slice.length < 2) continue;
    let km = 0;
    for (let i = 0; i < slice.length - 1; i++) km += haversineKm(slice[i], slice[i + 1]);
    stages.push({
      day: index + 1,
      from: slice[0].name,
      to: slice[slice.length - 1].name,
      approxKm: Math.round(km),
      waypoints: slice,
    });
  }
  return stages;
}

/** Sugestão honesta de durações típicas para o corredor. */
export function corridorDurationOptions(corridor: CuratedCorridor): CorridorDurationPreset[] {
  return corridor.durationPresets;
}

export type CorridorComplianceIssue = {
  code: "off_corridor" | "repeated_base" | "backwards" | "missing_endpoint";
  message: string;
  city?: string;
  deviationKm?: number;
};

/**
 * Verificação final: as bases diárias têm de pertencer ao corredor, avançar
 * sempre no mesmo sentido e não repetir a mesma cidade em dias seguidos.
 */
export function checkCorridorCompliance(
  corridor: CuratedCorridor,
  days: Array<{ city: string; lat?: number; lng?: number }>,
): CorridorComplianceIssue[] {
  const issues: CorridorComplianceIssue[] = [];
  const order = new Map(
    corridor.waypoints.map((waypoint, index) => [fold(waypoint.name), index]),
  );
  let lastIndex = -1;
  let lastCity = "";
  for (const day of days) {
    const key = fold(day.city);
    if (key && key === lastCity) {
      issues.push({
        code: "repeated_base",
        city: day.city,
        message: `A base "${day.city}" repete-se em dias consecutivos: o corredor tem de progredir.`,
      });
    }
    lastCity = key;
    const index = order.get(key);
    if (index == null) {
      if (day.lat != null && day.lng != null) {
        const deviation = deviationFromCorridorKm(corridor, { lat: day.lat, lng: day.lng });
        if (deviation > corridor.maxDeviationKm) {
          issues.push({
            code: "off_corridor",
            city: day.city,
            deviationKm: deviation,
            message: `"${day.city}" está a ~${deviation} km do percurso oficial da ${corridor.name} (máximo ${corridor.maxDeviationKm} km).`,
          });
        }
      }
      continue;
    }
    if (index < lastIndex) {
      issues.push({
        code: "backwards",
        city: day.city,
        message: `"${day.city}" faz o percurso recuar: a ${corridor.name} tem de ser seguida numa só direção.`,
      });
    }
    lastIndex = Math.max(lastIndex, index);
  }
  return issues;
}
